/** Wide two-panel user picker — the console default for choosing users.
 *
 * The console is multitenant: the same email routinely exists under several
 * tenants, so an email-only dropdown is ambiguous. Left panel = the email
 * list (as a plain autocomplete would show); right panel = details of the
 * option under the cursor / keyboard highlight: tenant, name, email, phone,
 * and Access-Group membership. Same two-pane pattern as the "Add property"
 * picker on the tour-operator Assisted config tab (context carries the
 * hovered option across the Popper portal; the Paper component is
 * module-level so the listbox isn't remounted on hover changes).
 *
 * AG membership is fetched per hovered user from the lightweight
 * `/users/{id}/agroups/` endpoint (PK-indexed M2M — real-time safe at any
 * user count). The hover is debounced and responses are cached by user id,
 * so arrowing through the list doesn't hammer the backend.
 */
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import * as usersApi from '@/api/users';
import type { ConsoleUserListItem, UserKind } from '@/api/users';

/** Minimal value shape — callers may store just {id, email}. */
export interface UserPickerValue {
  id: number;
  email: string;
}

const HOVER_DEBOUNCE_MS = 250;
const SEARCH_DEBOUNCE_MS = 200;
const PER_KIND_LIMIT = 10;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box sx={{ mb: 0.75 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function UserDetailsPane({ user }: { user: ConsoleUserListItem }) {
  // Debounce so arrowing through the list doesn't fire a request per row;
  // responses cache by user id (staleTime) across hovers.
  const settledId = useDebouncedValue(user.id, HOVER_DEBOUNCE_MS);
  const agroups = useQuery({
    queryKey: ['user-agroups', settledId],
    queryFn: () => usersApi.fetchAgroups(settledId),
    enabled: settledId === user.id,
    staleTime: 60_000,
  });

  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return (
    <Box sx={{ p: 1.5 }}>
      <DetailRow
        label="Tenant"
        value={
          user.tour_operator
            ? `${user.tour_operator.name}${user.tour_operator.code ? ` (${user.tour_operator.code})` : ''}`
            : 'Console (no tenant)'
        }
      />
      <DetailRow label="Name" value={name || '—'} />
      <DetailRow label="Email" value={user.email} />
      <DetailRow label="Phone" value={user.phone || '—'} />
      <Typography variant="caption" color="text.secondary" display="block">
        Access groups
      </Typography>
      {agroups.isPending ? (
        <CircularProgress size={14} sx={{ mt: 0.5 }} />
      ) : agroups.isError ? (
        <Typography variant="body2" color="text.secondary">
          unavailable
        </Typography>
      ) : agroups.data.results.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          none
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {agroups.data.results.map((ag) => (
            <Chip
              key={ag.id}
              size="small"
              variant="outlined"
              color={ag.full_access ? 'secondary' : 'default'}
              label={ag.name}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

const HoverContext = createContext<ConsoleUserListItem | null>(null);

function UserPickerPaper({ children, ...paperProps }: HTMLAttributes<HTMLElement>) {
  const hovered = useContext(HoverContext);
  return (
    <Paper
      {...paperProps}
      sx={{
        width: '100%',
        display: 'flex',
        overflow: 'hidden',
        '& .MuiAutocomplete-listbox': { maxHeight: 380 },
      }}
    >
      <Box sx={{ flex: '0 0 52%', minWidth: 0, borderRight: 1, borderColor: 'divider' }}>
        {children}
      </Box>
      <Box sx={{ flex: '1 1 48%', minWidth: 0, maxHeight: 380, overflowY: 'auto' }}>
        {hovered ? (
          <UserDetailsPane user={hovered} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Hover or arrow to a user to see tenant, name, contacts, and access
            groups.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

interface Props {
  value: UserPickerValue | null;
  /** Emits the full list item on selection (or null on clear). */
  onChange: (user: ConsoleUserListItem | null) => void;
  /** Which user populations to search. Default: task-capable users —
   *  console (system) + dashboard staff. Guests are opt-in. */
  kinds?: UserKind[];
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  /** Popper width (two panes need room). */
  popupWidth?: number;
}

export function UserPicker({
  value,
  onChange,
  kinds = ['system', 'staff'],
  label = 'User',
  placeholder = 'Any user',
  size = 'small',
  popupWidth = 640,
}: Props) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<ConsoleUserListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState<ConsoleUserListItem | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const kindsKey = kinds.join(',');

  useEffect(() => {
    const trimmed = input.trim();
    const t = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const pages = await Promise.all(
          kindsKey.split(',').map((kind) =>
            usersApi.list({
              kind: kind as UserKind,
              search: trimmed,
              pageSize: PER_KIND_LIMIT,
            }),
          ),
        );
        if (ctrl.signal.aborted) return;
        const seen = new Set<number>();
        const merged: ConsoleUserListItem[] = [];
        for (const page of pages) {
          for (const u of page.results) {
            if (seen.has(u.id)) continue;
            seen.add(u.id);
            merged.push(u);
          }
        }
        setOptions(merged);
      } catch {
        // aborts / transient network errors — the next keystroke retries
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [input, kindsKey]);

  return (
    <HoverContext.Provider value={hovered}>
      <Autocomplete<ConsoleUserListItem>
        size={size}
        options={options}
        value={(value as ConsoleUserListItem | null) ?? null}
        onChange={(_, v) => onChange(v)}
        onInputChange={(_, v) => setInput(v)}
        onHighlightChange={(_, o) => setHovered(o)}
        onClose={() => setHovered(null)}
        getOptionLabel={(u) => u.email}
        renderOption={(props, u) => (
          <Box component="li" {...props} key={u.id} sx={{ fontSize: 14, minWidth: 0 }}>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {u.email}
            </Box>
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.75, whiteSpace: 'nowrap' }}
            >
              [{u.tour_operator?.code ?? 'console'}]
            </Typography>
          </Box>
        )}
        // Server-side search is authoritative (matches phone/name too).
        filterOptions={(opts) => opts}
        // Fall back to email equality for URL-restored values that only carry
        // the email (id 0) — e.g. the audit page's ?actor_email= param.
        isOptionEqualToValue={(a, b) =>
          a.id && b.id ? a.id === b.id : a.email === b.email
        }
        loading={loading}
        slotProps={{ popper: { style: { width: popupWidth } } }}
        PaperComponent={UserPickerPaper}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </HoverContext.Provider>
  );
}
